/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import path from 'path';

import { defineComponentMapping } from '../import-components';
import { definePreconnectComponentMapping } from '../import-preconnect-components';
import {
  canUseDOM,
  CustomModelClient,
  respGridUtil,
  getEnvProp,
  UserSessionProvider,
  populatePIdataLayer,
  DynamicDCAPageProvider,
} from '@marriott/mi-headless-utils';
import { createPageModelStore, PageModelContext, PageModelState, StoreApi } from '@marriott/mi-store-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import { ACDLAnalyticsWrapper } from '@marriott/mi-ppv5-components';
import './styles.scss';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
    dataLayer?: Record<string, unknown>;
    targetGlobalSettings: Record<string, unknown>;
  }
}

if (canUseDOM) window.$ = window.jQuery = require('jquery-slim/dist/jquery.slim.min.js');

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = function (props: any) {
  const { Component, pageProps } = props;
  const { model, isAuthorMode, featureFlags, apolloEnvVars = {} } = pageProps;
  const { experience_mcom_carousel_lightining_variants: ppv5CarouselFeatureFlags = {} } = featureFlags || {};
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const store = useRef<StoreApi<PageModelState>>();
  const initialPageModel = {
    pageModel: respGridModel,
  };

  if (!store.current) {
    store.current = createPageModelStore(initialPageModel);
  }

  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  const envVars = {
    PPv5: 'true',
    ...apolloEnvVars,
    NEXT_PUBLIC_ENABLE_WEBSDK:
      getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true',
    NEXT_PUBLIC_ENABLE_DCA_V2: getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
  setEnvVars?.(envVars);

  //This code will populate the PI data related datalayer elements on clientside
  if (canUseDOM) populatePIdataLayer();

  const actionTypeQuery = pageProps?.query?.AT ?? pageProps?.query?.at;
  const atValue = Array.isArray(actionTypeQuery) ? actionTypeQuery[0] : actionTypeQuery;
  const isActionTypeConnect = typeof atValue === 'string' && atValue.toUpperCase() === 'CONNECT';

  if (isActionTypeConnect) {
    definePreconnectComponentMapping(allowedComponents, isAuthorMode, pageProps, ppv5CarouselFeatureFlags);
  } else {
    defineComponentMapping(allowedComponents, isAuthorMode, pageProps, ppv5CarouselFeatureFlags);
  }

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <PageModelContext.Provider value={store.current}>
        <ACDLAnalyticsWrapper {...pageProps} />
        <StyleSheetManager
          stylisPlugins={model?.rtlEnable === 'true' || pageProps?.rtlEnable === 'true' ? [rtlPluginTs] : []}
        >
          <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
        </StyleSheetManager>
      </PageModelContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
